import { forwardRef } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { toSearch } from '../../../../links'
import { queryParam } from '../../../../pages/SearchPage/query-params'
import toSearchParams from '../../../utils/toSearchParams'
import AutoSuggestCategory from './AutoSuggestCategory'
import { LinkListSkeleton } from '../../Skeleton/Skeleton'
import LinkButton from '../../Links/LinkButton'
import type { DataType, DataSubtype } from '../../../../pages/SearchPage/types'
import Paragraph from '../../Paragraph/Paragraph'

const NoResults = styled(Paragraph)`
  margin: 1rem 0 2rem;
`

const ResetFilterButton = styled(LinkButton)`
  margin-top: 1.5rem;
  padding: 0;
  font-size: 1.125rem;
  line-height: 1.6;
  color: #004699;
  text-decoration: none;

  &:hover,
  &.auto-suggest__dropdown-item--active {
    font-weight: inherit;
    color: #004699;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`

const AutoSuggestDropDown = styled.div<{ dropdownTop?: number }>`
  background: #fff;
  position: absolute;
  display: block;
  padding: 1rem;
  max-height: calc(100vh - 4rem - ${({ dropdownTop }) => dropdownTop}px);
  width: calc(100% + 2rem); // plus padding
  overflow: scroll;
  top: 3.75rem; // header height
  border-bottom: solid #000 0.25rem;

  @media screen and (min-width: 48rem) {
    padding: 1.5rem;
    width: 100%;
  }
`

const SkeletonWrapper = styled.div`
  padding: 0 1rem;
`

export interface AutoSuggestSearchResult {
  type: string
  label: string
  totalResults: number
  content: AutoSuggestSearchContent[]
}

export interface AutoSuggestSearchContent {
  category: string
  label: string
  uri: string
  type: DataType
  subType?: DataSubtype
  id: string
  name?: string
}

export interface AutoSuggestProps {
  suggestions: AutoSuggestSearchResult[]
  loading: boolean
  highlightValue: string
  inputValue: string
  toggleMenu?: () => void
}

const AutoSuggest = forwardRef<HTMLDivElement, AutoSuggestProps>(
  ({ suggestions = [], loading, highlightValue, inputValue, toggleMenu }, ref) => {
    const navigate = useNavigate()
    const totalNumberOfResults = suggestions.reduce(
      (previousValue, suggestion) => previousValue + suggestion.totalResults,
      0,
    )
    const dropdown = document.getElementById('autoSuggestDropdown')
    const dropdownTop = dropdown?.getBoundingClientRect().top

    return (
      <AutoSuggestDropDown
        id="autoSuggestDropdown"
        ref={ref}
        data-testid="autoSuggestDropdown"
        className="autoSuggestDropdown"
        dropdownTop={dropdownTop}
      >
        {loading && (
          <SkeletonWrapper>
            <LinkListSkeleton limit={6} />
          </SkeletonWrapper>
        )}
        {!loading &&
          suggestions.map((category) => (
            <AutoSuggestCategory
              key={category.label}
              highlightValue={highlightValue}
              category={category}
              inputValue={inputValue}
              toggleMenu={toggleMenu}
            />
          ))}
        {!loading && !suggestions.length && (
          <NoResults small>
            Er zijn geen resultaten gevonden. Probeer je zoekterm aan te passen.
          </NoResults>
        )}
        {!loading && suggestions.length > 0 && (
          <ResetFilterButton
            data-testid="allResults"
            onClick={() => {
              const { pathname } = toSearch()
              const queryString = toSearchParams([[queryParam, inputValue]]).toString()
              navigate(`${pathname as string}?${queryString}`)
            }}
          >
            Alle Resultaten binnen deze zoekterm ({totalNumberOfResults})
          </ResetFilterButton>
        )}
      </AutoSuggestDropDown>
    )
  },
)

export default AutoSuggest
